import { useEffect, useState } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DomainSelector,
  TextField,
  useMessage,
  useContentRefreshDomains,
} from '@pepper/ui'
import { attachDomainWithProject } from '@pepper/utils/src/api/seo'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { createProject } from 'utils/api/project'

import schema from './schema'

import s from './CreateProjectForm.module.css'

type CreateProjectFormProps = {
  onClose: () => void
  onCreate: (projectId: string) => void
  footerAction?: React.ReactNode
  domainId?: string
}

type FormValues = {
  project_title: string
}

export const CreateProjectForm = ({
  onClose,
  onCreate,
  footerAction,
  domainId,
}: CreateProjectFormProps) => {
  const companyId = useSelectedCompanyId()
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useMessage()

  const { data: domains } = useContentRefreshDomains(companyId)
  const [selectedDomain, setSelectedDomain] = useState<string | undefined>(
    domainId
  )

  const initialValues = {
    project_title: '',
  }

  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    const response = await createProject(values.project_title, companyId)
    if (response.status === 'ok') {
      if (selectedDomain) {
        const attachDomainResponse = await attachDomainWithProject({
          companyId,
          projectId: response.data.project_id,
          domainId: selectedDomain,
        })
        if (attachDomainResponse.status === 'ok') {
          onCreate(response.data.project_id)
          showSuccess('Project created successfully')
          onClose()
          actions.resetForm()
        } else {
          showError('Unable to link domain with the Project')
        }
      } else {
        showSuccess('Project created successfully')
      }

      await queryClient.invalidateQueries({ queryKey: ['/projects'] })
      await queryClient.invalidateQueries({ queryKey: ['project-domain-map'] })
      onClose()
    } else {
      showError(response?.error)
    }
  }

  useEffect(() => {
    if (!domainId && domains.length > 0) {
      setSelectedDomain(domains[0].id)
    }
  }, [domains?.length])

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isInitialValid={false}
      validateOnBlur={false}
    >
      {({ isSubmitting, isValid }) => (
        <Form>
          <div className={s.fieldGroup}>
            <TextField
              name="project_title"
              required={true}
              label="Project Name"
              fieldProps={{
                placeholder: 'Enter project name',
                autoFocus: true,
              }}
            />
          </div>
          <div className={s.fieldGroup}>
            <label className={s.label} htmlFor="project_domain">
              Project Domain
            </label>
            <div className={s.domainSelect}>
              <DomainSelector
                disabled={!!domainId}
                companyId={companyId}
                size="regular"
                isControlled={true}
                value={selectedDomain}
                onChange={setSelectedDomain}
              />
            </div>
          </div>

          <div className={s.footer}>
            {footerAction}
            <div className={s.right}>
              <Button type="text" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={!isValid}
                loading={isSubmitting}
              >
                Create Project
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
